import React from 'react'
import { useTranslation } from 'react-i18next'
import { formatDc3ThematicAreaForDisplay } from 'config/dc3ThematicAreas'
import { splitCharacters, splitDateParts } from './buildDc3DummyData'
import { IDc3CertificateData } from './types'
import {
  CombCharGrid,
  CombCharSlot,
  ClosingSection,
  ClosingSectionBody,
  Declaration,
  ExecutionPeriodFromLabel,
  ExecutionPeriodSeparator,
  ExecutionPeriodValue,
  FieldLabel,
  Header,
  Section,
  SectionFieldValue,
  SectionHeader,
  SectionTableBody,
  SectionTableCell,
  SectionTableRow,
  SignatureBlock,
  SignatureFooter,
  SignatureImage,
  SignatureImageArea,
  SignatureLine,
  SignatureName,
  SignatureRoleLabel,
  SignaturesRow,
  TemplateRoot,
} from './Dc3CertificateTemplate.styled'

interface IProps {
  data: IDc3CertificateData
}

const prefix = 'dc3Certificate.template'
const CURP_BOX_COUNT = 18
const RFC_BOX_COUNT = 13
const PATRONAL_REGISTRATION_BOX_COUNT = 11
const DATE_YEAR_SLOTS = 4
const DATE_MONTH_SLOTS = 2
const DATE_DAY_SLOTS = 2

const renderCombCharBoxes = (
  value: string,
  length: number,
  keyPrefix = 'comb',
  options?: { dateField?: boolean; fixedSlots?: boolean; contentOnly?: boolean }
) => {
  const dateField = options?.dateField ?? false
  const fixedSlots = options?.fixedSlots ?? dateField
  const contentOnly = options?.contentOnly ?? false
  const chars = splitCharacters(value, length, { contentOnly })

  return (
    <CombCharGrid $fixedSlots={fixedSlots} $centerFixedSlots={dateField}>
      {chars.map((char, index) => (
        <CombCharSlot
          key={`${keyPrefix}-${index}`}
          $fixedSlots={fixedSlots}
          $regular={dateField}
        >
          {char}
        </CombCharSlot>
      ))}
    </CombCharGrid>
  )
}

const Dc3CertificateTemplate: React.FC<IProps> = ({ data }) => {
  const { t } = useTranslation()
  const { attestation, course, worker, executionPeriod } = data
  const hours =
    course.hours != null ? String(course.hours).replace(/\.0$/, '') : ''
  const start = splitDateParts(executionPeriod.startDate)
  const finish = splitDateParts(executionPeriod.finishDate)

  const renderSignature = (
    name: string,
    signatureUrl: string | null,
    roleLabel: string
  ) => (
    <SignatureBlock>
      <SignatureRoleLabel>{roleLabel}</SignatureRoleLabel>
      <SignatureImageArea>
        {signatureUrl ? <SignatureImage src={signatureUrl} alt="" /> : null}
      </SignatureImageArea>
      <SignatureName>{name}</SignatureName>
      <SignatureLine />
      <SignatureFooter>
        {t(`${prefix}.closing.signatures.nameAndSignature`)}
      </SignatureFooter>
    </SignatureBlock>
  )

  return (
    <TemplateRoot data-dc3-template="true">
      <Header>
        <h1>{t(`${prefix}.header.formatLabel`)}</h1>
        <h2>{t(`${prefix}.header.certificateLabel`)}</h2>
      </Header>

      <Section>
        <SectionHeader>{t(`${prefix}.sections.worker`)}</SectionHeader>
        <SectionTableBody>
          <SectionTableRow>
            <SectionTableCell>
              <FieldLabel>{t(`${prefix}.fields.workerName`)}</FieldLabel>
              <SectionFieldValue>{worker.fullName}</SectionFieldValue>
            </SectionTableCell>
          </SectionTableRow>
          <SectionTableRow>
            <SectionTableCell $half $combField>
              <FieldLabel>
                {t(`${prefix}.fields.uniquePopulationRegistry`)}
              </FieldLabel>
              {renderCombCharBoxes(worker.curp, CURP_BOX_COUNT, 'curp', {
                fixedSlots: true,
              })}
            </SectionTableCell>
            <SectionTableCell $half>
              <FieldLabel>
                {t(`${prefix}.fields.specificOccupationCatalog`)}
              </FieldLabel>
              <SectionFieldValue>{worker.occupation}</SectionFieldValue>
            </SectionTableCell>
          </SectionTableRow>
          <SectionTableRow>
            <SectionTableCell>
              <FieldLabel>{t(`${prefix}.fields.position`)}</FieldLabel>
              <SectionFieldValue>{worker.position}</SectionFieldValue>
            </SectionTableCell>
          </SectionTableRow>
        </SectionTableBody>
      </Section>

      <Section>
        <SectionHeader>{t(`${prefix}.sections.company`)}</SectionHeader>
        <SectionTableBody>
          <SectionTableRow>
            <SectionTableCell>
              <FieldLabel>{t(`${prefix}.fields.companyName`)}</FieldLabel>
              <SectionFieldValue>
                {attestation.companyFiscalName}
              </SectionFieldValue>
            </SectionTableCell>
          </SectionTableRow>
          <SectionTableRow>
            <SectionTableCell $half $combField>
              <FieldLabel>
                {t(`${prefix}.fields.federalTaxpayerRegistryHomoclave`)}
              </FieldLabel>
              {renderCombCharBoxes(
                attestation.companyRfc,
                RFC_BOX_COUNT,
                'rfc',
                {
                  fixedSlots: true,
                  contentOnly: true,
                }
              )}
            </SectionTableCell>
            <SectionTableCell $half $combField>
              <FieldLabel>
                {t(`${prefix}.fields.patronalRegistration`)}
              </FieldLabel>
              {renderCombCharBoxes(
                attestation.patronalRegistration,
                PATRONAL_REGISTRATION_BOX_COUNT,
                'patronal',
                { fixedSlots: true }
              )}
            </SectionTableCell>
          </SectionTableRow>
          <SectionTableRow>
            <SectionTableCell>
              <FieldLabel>{t(`${prefix}.fields.companyActivity`)}</FieldLabel>
              <SectionFieldValue>
                {attestation.companyActivity}
              </SectionFieldValue>
            </SectionTableCell>
          </SectionTableRow>
        </SectionTableBody>
      </Section>

      <Section>
        <SectionHeader>
          {t(`${prefix}.sections.trainingProgramProductivity`)}
        </SectionHeader>
        <SectionTableBody>
          <SectionTableRow>
            <SectionTableCell>
              <FieldLabel>{t(`${prefix}.fields.courseName`)}</FieldLabel>
              <SectionFieldValue>{course.name}</SectionFieldValue>
            </SectionTableCell>
          </SectionTableRow>
          <SectionTableRow>
            <SectionTableCell $duration>
              <FieldLabel>{t(`${prefix}.fields.courseHours`)}</FieldLabel>
              <SectionFieldValue>{hours}</SectionFieldValue>
            </SectionTableCell>
            <SectionTableCell $periodIntro>
              <FieldLabel>
                {t(`${prefix}.fields.executionPeriodLine1`)}
              </FieldLabel>
              <ExecutionPeriodValue>
                <span>{t(`${prefix}.fields.executionPeriodLine2`)}:</span>
                <ExecutionPeriodFromLabel>
                  {t(`${prefix}.dates.from`)}
                </ExecutionPeriodFromLabel>
              </ExecutionPeriodValue>
            </SectionTableCell>
            <SectionTableCell
              $dateCombSlots={DATE_YEAR_SLOTS}
              $centered
              $combField
            >
              <FieldLabel>{t(`${prefix}.dates.year`)}</FieldLabel>
              {renderCombCharBoxes(start.year, DATE_YEAR_SLOTS, 'start-year', {
                dateField: true,
              })}
            </SectionTableCell>
            <SectionTableCell
              $dateCombSlots={DATE_MONTH_SLOTS}
              $centered
              $combField
            >
              <FieldLabel>{t(`${prefix}.dates.month`)}</FieldLabel>
              {renderCombCharBoxes(
                start.month,
                DATE_MONTH_SLOTS,
                'start-month',
                {
                  dateField: true,
                }
              )}
            </SectionTableCell>
            <SectionTableCell
              $dateCombSlots={DATE_DAY_SLOTS}
              $centered
              $combField
            >
              <FieldLabel>{t(`${prefix}.dates.day`)}</FieldLabel>
              {renderCombCharBoxes(start.day, DATE_DAY_SLOTS, 'start-day', {
                dateField: true,
              })}
            </SectionTableCell>
            <SectionTableCell $narrow $periodSeparator>
              <ExecutionPeriodSeparator>
                {t(`${prefix}.dates.to`)}
              </ExecutionPeriodSeparator>
            </SectionTableCell>
            <SectionTableCell
              $dateCombSlots={DATE_YEAR_SLOTS}
              $centered
              $combField
            >
              <FieldLabel>{t(`${prefix}.dates.year`)}</FieldLabel>
              {renderCombCharBoxes(
                finish.year,
                DATE_YEAR_SLOTS,
                'finish-year',
                { dateField: true }
              )}
            </SectionTableCell>
            <SectionTableCell
              $dateCombSlots={DATE_MONTH_SLOTS}
              $centered
              $combField
            >
              <FieldLabel>{t(`${prefix}.dates.month`)}</FieldLabel>
              {renderCombCharBoxes(
                finish.month,
                DATE_MONTH_SLOTS,
                'finish-month',
                { dateField: true }
              )}
            </SectionTableCell>
            <SectionTableCell
              $dateCombSlots={DATE_DAY_SLOTS}
              $centered
              $combField
            >
              <FieldLabel>{t(`${prefix}.dates.day`)}</FieldLabel>
              {renderCombCharBoxes(finish.day, DATE_DAY_SLOTS, 'finish-day', {
                dateField: true,
              })}
            </SectionTableCell>
          </SectionTableRow>
          <SectionTableRow>
            <SectionTableCell>
              <FieldLabel>
                {t(`${prefix}.fields.courseThematicArea`)}
              </FieldLabel>
              <SectionFieldValue>
                {formatDc3ThematicAreaForDisplay(attestation.thematicArea)}
              </SectionFieldValue>
            </SectionTableCell>
          </SectionTableRow>
          <SectionTableRow>
            <SectionTableCell>
              <FieldLabel>
                {t(`${prefix}.fields.trainingAgentNameOrStps`)}
              </FieldLabel>
              <SectionFieldValue>
                {attestation.trainingAgentName}
              </SectionFieldValue>
            </SectionTableCell>
          </SectionTableRow>
        </SectionTableBody>
      </Section>

      <ClosingSection>
        <ClosingSectionBody>
          <Declaration>{t(`${prefix}.closing.truthDeclaration`)}</Declaration>

          <SignaturesRow>
            {renderSignature(
              attestation.instructorName,
              attestation.instructorSignatureUrl,
              t(`${prefix}.closing.signatures.instructorOrTutor`)
            )}
            {renderSignature(
              attestation.legalRepresentativeName,
              attestation.legalRepresentativeSignatureUrl,
              t(`${prefix}.closing.signatures.employerOrLegalRepresentative`)
            )}
            {renderSignature(
              attestation.workersRepresentativeName,
              attestation.workersRepresentativeSignatureUrl,
              t(`${prefix}.closing.signatures.workersRepresentativeRole`)
            )}
          </SignaturesRow>
        </ClosingSectionBody>
      </ClosingSection>
    </TemplateRoot>
  )
}

export default Dc3CertificateTemplate
